import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { callFunction } from "@/utils/callFunction";
import type { Product } from "@/types";

const CACHE_KEY = "homepage_data";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutos

interface HomepageData {
  categoryNames: string[];
  featured: Product[];
  showcase: Record<string, Product>;
  categoryCounts: Record<string, number>;
}

interface HomepageProductsContextValue {
  categoryNames: string[];
  featured: Product[];
  showcase: Record<string, Product>;
  categoryCounts: Record<string, number>;
  loading: boolean;
}

const HomepageProductsContext = createContext<HomepageProductsContextValue>({
  categoryNames: [],
  featured: [],
  showcase: {},
  categoryCounts: {},
  loading: true,
});

export function useHomepageProducts() {
  return useContext(HomepageProductsContext);
}

function getCachedData(): HomepageData | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL_MS) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedData(data: HomepageData) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch { /* ignore quota errors */ }
}

export function HomepageProductsProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<HomepageData>(() => {
    const cached = getCachedData();
    return cached ?? { categoryNames: [], featured: [], showcase: {}, categoryCounts: {} };
  });
  const [loading, setLoading] = useState(!getCachedData());

  useEffect(() => {
    const cached = getCachedData();
    if (cached) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function load() {
      try {
        const result = await callFunction<HomepageData>("getHomepageData");
        if (!cancelled) {
          setData(result);
          setCachedData(result);
        }
      } catch (err) {
        console.error("Erro ao buscar dados da homepage:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <HomepageProductsContext.Provider value={{ ...data, loading }}>
      {children}
    </HomepageProductsContext.Provider>
  );
}
